import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/system';
import { Feedback, Footer, Header, Img, LocationTracker, MainLoading, Page } from 'components';
import { actions, Store } from 'rdx';
import { FC, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useSearchParams } from 'react-router-dom';
import { bulls } from 'services';
import { Breed, BullListItem, LocationTrackerItem, Segment } from 'types';
import utils from 'utils';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { GetSummaryResponse } from 'services/bulls/bulls';
import { applyFixedBreedMetadata } from 'config/breedMetadata';

const normStr = utils.string.normalize;
const SUMMARY_CACHE_KEY = 'crv-bull-summary-v5';

const dictionary = {
  catalog: 'Catálogo',
  'bull-search': 'Busca de touros',
  'consultant-search': 'Busca de consultores'
};

const breedsHighligted = [
  'aberdeen angus',
  'gir leiteiro',
  'holandês preto e branco',
  'nelore mocho',
  'nelore',
  'brangus',
  'nelore po'
];

const toPaddedStr = (n: number) => n.toString().padStart(2, '0');

const getBullTitle = (bull: BullListItem) =>
  (bull?.warName != '-' ? bull?.warName : bull.name) ?? '';

const getSummaryImageUrl = (bull: BullListItem) => {
  if ('image' in bull) {
    return typeof bull.image === 'string' ? bull.image : bull.image?.filepath;
  }
  return undefined;
};

const deriveBreedsFromSummaries = (bulls: BullListItem[]): Breed[] => {
  const byBreed = new Map<string, Breed>();

  bulls.forEach((bull) => {
    if (!bull.breed) return;
    const key = `${bull.type}-${bull.segment}-${bull.breed.toLowerCase()}`;
    if (byBreed.has(key)) return;

    const picture =
      getSummaryImageUrl(bull) ||
      ('images' in bull ? bull.images?.[0]?.filepath : undefined) ||
      ('picture' in bull ? bull.picture : undefined) ||
      '/imgs/default.png';

    byBreed.set(key, {
      id: key,
      name: bull.breed,
      type: bull.type,
      segment: bull.segment,
      picture,
      filters: []
    });
  });

  return Array.from(byBreed.values()).sort((a, b) =>
    a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  );
};

const getBullStamps = (bull: BullListItem): string[] =>
  bull.stamps ?? ('filters' in bull ? bull.filters?.stamps ?? [] : []);

const hasExpectedSummaryShape = (data: GetSummaryResponse) =>
  data.bulls.some((bull) => 'evaluations' in bull);

const getSummaryEvaluationNames = (bull: BullListItem): string[] => {
  if (!('filters' in bull)) return [];

  const { filters } = bull;
  const filterEvaluations = filters?.evaluations;
  const filterProofNames = filters?.proofNames;

  return [
    ...(('evaluations' in bull && Array.isArray(bull.evaluations)) ? bull.evaluations : []),
    ...(('proofNames' in bull && Array.isArray(bull.proofNames)) ? bull.proofNames : []),
    ...(Array.isArray(filterEvaluations) ? filterEvaluations : []),
    ...(Array.isArray(filterProofNames) ? filterProofNames : [])
  ];
};

const matchesRating = (bull: BullListItem, rating: string | null) => {
  if (rating === 'all' || rating === null) return true;

  const normalizedRating = normStr(rating);
  const summaryEvaluationNames = getSummaryEvaluationNames(bull);

  if (summaryEvaluationNames.length > 0) {
    return summaryEvaluationNames.some((proofName) => normStr(proofName) === normalizedRating);
  }

  if ('proof' in bull && bull.proof.length > 0) {
    return bull.proof.some((entry) =>
      Object.keys(entry ?? {}).some((proofName) => normStr(proofName) === normalizedRating)
    );
  }

  return false;
};

const matchesIndexFilter = (bull: BullListItem, indexFilter: string | null) => {
  if (!indexFilter || indexFilter === 'all' || indexFilter === '-') return true;
  return utils.bullIndices.bullHasIndex(bull, indexFilter);
};

const MainLayout2: FC = () => {
  
  const dispatch = useDispatch();
  const { scope, feedback, items, modal } = useSelector((store: Store) => store);
  const lastLengthSelected = useRef(0);
  const isPositiveRef = useRef(false);
  
  const [ searchParams ] = useSearchParams();
  
  const { pathname } = window.location;
  const gtMd = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  useEffect(() => {
    (async () => {
      if (items.selectedBulls.length !== lastLengthSelected.current) {
        if (feedback.bottomAlert.visible) {
          dispatch(actions.feedback.hideBottomAlert());
          await new Promise(f => setTimeout(f, 300));
        }
        const diff = items.selectedBulls.length - lastLengthSelected.current;
        isPositiveRef.current = diff > 0;
        const op = diff > 0 ? 'add' : 'rm';
        const elements = Math.abs(diff);
        const remaining = lastLengthSelected.current - elements;
        const text = op === 'add'
          ? `Você selecionou ${toPaddedStr(elements)} touro${elements > 1 ? 's' : ''} para gerar o catálogo personalizado`
          : [
            `Você retirou ${toPaddedStr(elements)} touro${elements > 1 ? 's' : ''} da lista`,
            ...(
              remaining === 0
                ? []
                : [ `Resta${remaining > 1 ? 'm' : ''} ${toPaddedStr(remaining)} touro${remaining > 1 ? 's' : ''}` ]
            )
          ];
        dispatch(actions.feedback.setBottomAlertText(text));
        dispatch(actions.feedback.showBottomAlert(dispatch));

        lastLengthSelected.current = items.selectedBulls.length;
      }
    })();
  }, [ items.selectedBulls ]);

  useEffect(() => {
    dispatch(actions.items.setMainLoading(true));

    const hydrateSummaries = (data: GetSummaryResponse) => {
      const { bulls, breedsProofs } = data;
      const sortedBulls = bulls.sort((a, b) =>
        normStr(getBullTitle(a)) < normStr(getBullTitle(b))
          ? -1
          : normStr(getBullTitle(a)) > normStr(getBullTitle(b)) ? 1 : 0
      );
      const breeds = applyFixedBreedMetadata(
        breedsProofs?.length ? breedsProofs : deriveBreedsFromSummaries(sortedBulls)
      );

      dispatch(actions.items.setBulls(sortedBulls));
      dispatch(actions.items.setBreeds(breeds));
      const highlight = breeds.filter(
        b => breedsHighligted.includes(b.name.toLowerCase())
      ).map(b => breeds.findIndex(e => e.name === b.name));
      dispatch(actions.items.setHighlighBreeds(highlight));
    };

    try {
      const cachedSummary = window.localStorage.getItem(SUMMARY_CACHE_KEY);
      if (cachedSummary) {
        const parsedSummary = JSON.parse(cachedSummary);
        if (hasExpectedSummaryShape(parsedSummary)) {
          hydrateSummaries(parsedSummary);
          dispatch(actions.items.setMainLoading(false));
        }
      }
    } catch (err) {
      console.warn('Unable to read cached bull summary', err);
    }

    bulls.getSummaries()
      .then((res) => {
        hydrateSummaries(res.data);
        try {
          window.localStorage.setItem(SUMMARY_CACHE_KEY, JSON.stringify(res.data));
        } catch (err) {
          console.warn('Unable to cache bull summary', err);
        }
        setTimeout(() => dispatch(actions.items.setMainLoading(false)), 500);
      })
      .catch(err => {
        console.log({ err });
        setTimeout(() => dispatch(actions.items.setMainLoading(false)), 500);
      });
  }, [ searchParams ]);

  useEffect(() => {
    dispatch(actions.scope.changeRating('all'));
    dispatch(actions.scope.changeNewRelease('-'));
    dispatch(actions.scope.changeIndexFilter('all'));
  }, [ scope.breed, scope.segment, scope.filterValue ]);

  useEffect(() => {
    const scopedBulls = items.bulls.reduce(
      (acc: number[], bull, index) =>
        (
          utils.segmentFromBullOrBreed(bull) === scope.segment &&
          (
            !scope.breed || // skip condition
            scope.breed.toLowerCase() === bull.breed?.toLowerCase?.()
          ) &&
          (
            (scope.rating === 'all' || scope.rating === null) || // skip condition
            matchesRating(bull, scope.rating)
          ) &&
          (
            (scope.stamps == null || scope.stamps.length == 0) ||
            (
              getBullStamps(bull).length > 0 &&
              getBullStamps(bull).map(s => scope.stamps.includes(s.toUpperCase())).filter(s => s == true).length == scope.stamps.length
            )
          ) &&
          (
            (scope.nationality == null || scope.nationality == 'all' || (scope.breed != null && scope.breed.toLowerCase().indexOf('angus') == -1)) ||
            (
              !scope.nationality ||
              (bull.nationality != null && scope.nationality.toLowerCase() === bull.nationality.toLowerCase())
            )
          )
           && 
          (
            (scope.segment === 'zc' || scope.segment === 'ec')
              ? matchesIndexFilter(bull, scope.indexFilter)
              : true
          )
           && 
          (
            scope.newRelease === '-' || scope.newRelease === null || bull.newRelease?.toLowerCase() === scope.newRelease.toLowerCase()
          )
        )
          ? [ ...acc, index ]
          : acc,
      []
    );
    dispatch(actions.items.changeScopedBulls(scopedBulls));
  }, [ scope, items.bulls ]);

  let trackedLocation: LocationTrackerItem[] = [];

  const sections = (window.location.pathname ?? '').split('/');

  if (sections.includes('segment')) {
    const [ segment, breed, bullcode ] = sections.slice(2).filter(e => e !== 'bull-search');
    dispatch(actions.scope.changeSegment(
      (segment === '' ? '' : utils.segmentTranslator(segment, 'short')) as Segment
    ));
    dispatch(actions.scope.changeBreed(
      // eslint-disable-next-line no-extra-boolean-cast
      !!breed ? utils.string.pathlikeToPrintable(breed) : null
    ));
    const targetBull = items.bulls.find(b => utils.compareWithNullable(b.CRVcode, bullcode));
    dispatch(actions.scope.changeBull_crv_code(
      // eslint-disable-next-line no-extra-boolean-cast
      !!bullcode ? targetBull?.CRVcode || null : null
    ));
  } else {
    dispatch(actions.scope.changeBull_crv_code(null));
    dispatch(actions.scope.changeBreed(null));
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [ scope, pathname ]);

  if (scope.segment !== '') {
    const segmentTracking: LocationTrackerItem = {
      pathname: `/segment/${utils.segmentTranslator(scope.segment, 'pathlike')}`,
      printable: utils.segmentTranslator(scope.segment, 'printable'),
      segment: scope.segment
    };
    trackedLocation = [ segmentTracking ];
    if (scope.breed) {
      const breedTracking: LocationTrackerItem = {
        pathname: `/segment\
        /${utils.segmentTranslator(scope.segment, 'pathlike')}\
        /${utils.string.printableToPathlike(scope.breed)}`.replace(/(\s|\t)*/g, ''),
        printable: scope.breed,
        segment: scope.segment
      };
      trackedLocation = [ segmentTracking, breedTracking ];
      if (scope.bull) {
        const targetBull = items.bulls.find(b => b.CRVcode === scope.bull);
        const bullPrintableName = targetBull?.warName != '-' ? targetBull?.warName ?? '' : targetBull?.name ?? '';
        const bullTracking: LocationTrackerItem = {
          pathname: `/segment\
          /${utils.segmentTranslator(scope.segment, 'pathlike')}\
          /${utils.string.printableToPathlike(scope.breed)}\
          /${utils.string.printableToPathlike(bullPrintableName)}`
            .replace(/(\s|\t)*/g, ''),
          printable: bullPrintableName,
          segment: scope.segment
        };
        trackedLocation = [ segmentTracking, breedTracking, bullTracking ];
      }
    }
    if (sections.includes('catalog')) {
      const catalogTracking: LocationTrackerItem = {
        pathname: '/catalog',
        printable: 'Catálogo personalizado',
        segment: ''
      };
      trackedLocation = [ ...trackedLocation, catalogTracking ];
    }
    if (sections.includes('bull-search')) {
      const bullSearchTracking: LocationTrackerItem = {
        pathname: '/bull-search',
        printable: 'Busca de touros',
        segment: scope.segment
      };
      trackedLocation = [ ...trackedLocation, bullSearchTracking ];
    }
  } else {
    const paths = sections
      .filter(p => p !== '');

    if (paths.length > 0) {
      const tracking: LocationTrackerItem = {
        pathname: window.location.pathname,
        printable: utils.fromDictionary(dictionary, paths[0], utils.string.pathlikeToPrintable(paths[0])),
        segment: ''
      };
      trackedLocation = [ tracking ];
    }
  }

  useEffect(() => {
    dispatch(actions.feedback.setBottomAlertIconType(isPositiveRef.current ? 'ok' : 'error'));
  }, [ items.selectedBulls ]);

  return (
    <Page sx={{ paddingTop: gtMd ? '110px' : '110px' }}>
      <Header />
      <LocationTracker trackedLocation={trackedLocation} />
      {items.mainLoading && <MainLoading />}
      <Outlet />
      <Footer />
      <Feedback
        text={feedback.bottomAlert.text}
        onClose={() => dispatch(actions.feedback.hideBottomAlert())}
      />
      {
        modal.secImgBull.open &&
        <Box sx={{
          ...utils.sx.flex.col('center', 'center'),
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          top: 0, reight: 0,
          background: '#333333cc',
          zIndex: 9
        }}>
          <Box sx={{ width: '90%', maxWidth: '450px', aspectRatio: '3/2', position: 'relative' }}>
            <Stack {...utils.stack.flex.col('flex-start', 'flex-start')}>
              <Zoom>
                <Img
                  url={modal.secImgBull.url}
                  fallbackImg='/imgs/default.png'
                  sx={{ width: '100%' }}
                />
              </Zoom>
              {
                modal.secImgBull.subtitle &&
                modal.secImgBull.subtitle.trim() &&
                modal.secImgBull.subtitle.trim() !== '-' &&
                <Typography
                  sx={t => ({
                    width: '100%',
                    background: t.palette.primary.main,
                    color: 'white',
                    fontSize: '12px !important',
                    paddingX: '4px'
                  })}
                >
                  {modal.secImgBull.subtitle.trim()}
                </Typography>
              }
            </Stack>

            <Box
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: '#98002E',
                position: 'absolute',
                top: 0, right: 0,
                translate: '-50% 50%',
                border: '2px solid white',
                cursor: 'pointer',
                '::before': {
                  content: '""',
                  width: '2px',
                  height: '16px',
                  display: 'block',
                  background: 'white',
                  translate: '9px 2px',
                  rotate: '45deg'
                },
                '::after': {
                  content: '""',
                  width: '2px',
                  height: '16px',
                  display: 'block',
                  background: 'white',
                  translate: '9px -14px',
                  rotate: '-45deg'
                }
              }}
              onClick={() => dispatch(actions.modal.setSIBOpen(false))}
            />
          </Box>
        </Box>
      }
    </Page>
  );
};

export default MainLayout2;
